import React, { useContext, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import Avatar from "@material-ui/core/Avatar";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import TablePagination from "@material-ui/core/TablePagination";
import Tooltip from "@material-ui/core/Tooltip";
import Popover from "@material-ui/core/Popover";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isCompanyAdmin, isSystemOwner } from "../../helpers/userAccess";
import { i18n } from "../../translate/i18n";
import { getInitials } from "../../helpers/getInitials";
import api, { getProfilePicUrl } from "../../services/api";
import { getCampaignTimezone } from "../../helpers/getCampaignTimezone";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useDialogStyles } from "../../theme/redesign";
import { colorFromKey, countryFromPhone, daysLeft, defaultSymbol, flagEmoji, formatPhoneDisplay, joinPhone, PHONE_PREFIXES, PROFILE_PLACEHOLDER, setNegocioFormat, splitPhone, statusColor } from "./helpers";
import { useStyles } from "./styles";
import { useNegocioBridge } from "./useNegocioSync";
import { negocioResources, resourceKey, subscribeNegocio } from "./sync";

export const tNegocio = (k) => i18n.t(`negocioModule.${k}`);

const BOT_MARKUP =
  /\[\[emoji:\d{5,}:([^\]\n]{1,8})\]\]|\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~|`([^`\n]+)`|\|\|([^|\n]+)\|\|/g;

/** Texto con formato del bot (*negrita*, _cursiva_, ~tachado~, `código`, ||spoiler||, emojis) → React. */
export function renderBotMarkup(text, key = "bm") {
  const src = String(text || "");
  const out = [];
  const re = new RegExp(BOT_MARKUP.source, "g");
  let last = 0;
  let m;
  while ((m = re.exec(src))) {
    if (m.index > last) out.push(src.slice(last, m.index));
    const k = `${key}-${m.index}`;
    if (m[1] !== undefined) out.push(m[1]);
    else if (m[2] !== undefined) out.push(<strong key={k}>{renderBotMarkup(m[2], k)}</strong>);
    else if (m[3] !== undefined) out.push(<em key={k}>{renderBotMarkup(m[3], k)}</em>);
    else if (m[4] !== undefined) out.push(<s key={k}>{renderBotMarkup(m[4], k)}</s>);
    else if (m[5] !== undefined) out.push(<code key={k}>{m[5]}</code>);
    else out.push(<span key={k} style={{ background: "rgba(127,127,127,.35)", borderRadius: 3 }}>{renderBotMarkup(m[6], k)}</span>);
    last = re.lastIndex;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}

export function BotMarkup({ text, style }) {
  return <span style={{ whiteSpace: "pre-wrap", ...style }}>{renderBotMarkup(text)}</span>;
}

export function HintTip({ text }) {
  const [anchor, setAnchor] = useState(null);
  if (!text) return null;
  return (
    <>
      <IconButton
        type="button"
        size="small"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        style={{ padding: 2, marginLeft: 2, color: "#64748b" }}
        aria-label="info"
      >
        <InfoOutlinedIcon style={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div style={{ padding: "12px 14px", maxWidth: 280, fontSize: 13, lineHeight: 1.45 }}>
          {text}
        </div>
      </Popover>
    </>
  );
}

export const useMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("sm"));
};

export const phoneFormatHint = () => tNegocio("phoneFormat");

export function CountryFlag({ code, name, size = 18 }) {
  const label = name || tNegocio("pages.customers.unknownCountry");
  return (
    <Tooltip title={label}>
      <span
        className="countryFlag"
        role="img"
        aria-label={label}
        style={{ fontSize: size, lineHeight: 1, display: "inline-flex", cursor: "default" }}
      >
        {flagEmoji(code)}
      </span>
    </Tooltip>
  );
}

export function PhoneWithFlag({ number, countryCode, countryName }) {
  const classes = useStyles();
  const raw = String(number || "");
  if (!raw || /^tg:/i.test(raw)) return null;
  const fromNumber = countryFromPhone(raw);
  const code = countryCode || fromNumber.countryCode;
  const name = countryName || fromNumber.countryName;
  return (
    <span className={classes.phoneWithFlag}>
      <CountryFlag code={code} name={name} />
      <span>{formatPhoneDisplay(raw) || raw}</span>
    </span>
  );
}

export function PhoneFields({ value, onChange, disabled }) {
  const classes = useStyles();
  const parts = splitPhone(value);
  const prefixRow = PHONE_PREFIXES.find((row) => row.prefix === parts.prefix) || parts;
  return (
    <div className={classes.phoneFields}>
      <TextField
        select
        size="small"
        margin="dense"
        variant="outlined"
        label={tNegocio("fields.prefix")}
        value={parts.prefix}
        disabled={disabled}
        className={classes.phonePrefixField}
        InputLabelProps={{ shrink: true }}
        SelectProps={{
          renderValue: (selected) => {
            const row = PHONE_PREFIXES.find((p) => p.prefix === selected) || prefixRow;
            return (
              <span className={classes.phonePrefixValue}>
                <CountryFlag code={row.countryCode} name={row.countryName} size={16} />
                +{selected}
              </span>
            );
          },
        }}
        onChange={(e) => onChange(joinPhone(e.target.value, parts.local))}
      >
        {PHONE_PREFIXES.map((row) => (
          <MenuItem key={row.prefix} value={row.prefix}>
            <span className={classes.phonePrefixValue}>
              <CountryFlag code={row.countryCode} name={row.countryName} size={16} />
              +{row.prefix}
            </span>
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        margin="dense"
        variant="outlined"
        label={tNegocio("fields.phone")}
        value={parts.local}
        disabled={disabled}
        className={classes.phoneLocalField}
        onChange={(e) => {
          const digits = String(e.target.value || "").replace(/\D/g, "");
          const nested = PHONE_PREFIXES.find((row) => digits.startsWith(row.prefix) && digits.length > row.prefix.length + 5);
          onChange(nested ? digits : joinPhone(parts.prefix, digits));
        }}
      />
    </div>
  );
}

export const stopRow = (fn) => (e) => {
  if (e) e.stopPropagation();
  if (fn) fn(e);
};

const TRAFFIC = {
  active: { color: "#2e7d32", key: "pages.customers.kinds.active" },
  expiring: { color: "#ed6c02", key: "pages.customers.kinds.expiring" },
  expired: { color: "#c62828", key: "pages.customers.kinds.expired" },
  none: { color: "#ffb300", key: "pages.customers.kinds.none" },
};

const SERVICE_TRAFFIC_KEYS = {
  active: "status.active",
  expiring: "pages.customers.kinds.expiring",
  expired: "status.expired",
  none: "pages.customers.kinds.none",
};

export function TrafficLight({ kind, showLabel }) {
  const classes = useStyles();
  const item = TRAFFIC[kind] || TRAFFIC.none;
  const label = showLabel ? tNegocio(SERVICE_TRAFFIC_KEYS[kind] || item.key) : null;
  return (
    <Tooltip title={tNegocio(item.key)}>
      <span className={classes.trafficStatus}>
        <span className={classes.trafficDot} style={{ backgroundColor: item.color }} />
        {label}
      </span>
    </Tooltip>
  );
}

export function dueInfo(endDate, status) {
  if (status === "cancelled") return { kind: "none", text: tNegocio("status.cancelled") };
  const left = daysLeft(endDate);
  if (left == null) {
    if (status === "expired") return { kind: "expired", text: tNegocio("status.expired") };
    return { kind: "none", text: "—" };
  }
  if (left < 0) {
    const n = -left;
    return {
      kind: "expired",
      text: n === 1 ? tNegocio("due.expiredOne") : tNegocio("due.expiredN").replace("{n}", String(n)),
    };
  }
  if (left === 0) return { kind: "expired", text: tNegocio("due.today") };
  if (left === 1) return { kind: "expiring", text: tNegocio("due.tomorrow") };
  if (left <= 3) return { kind: "expiring", text: tNegocio("due.leftN").replace("{n}", String(left)) };
  return { kind: "active", text: tNegocio("due.leftN").replace("{n}", String(left)) };
}

export function DueBadge({ endDate, status }) {
  const classes = useStyles();
  const info = dueInfo(endDate, status);
  const item = TRAFFIC[info.kind] || TRAFFIC.none;
  return (
    <span className={classes.trafficStatus} style={{ color: item.color }}>
      <span className={classes.trafficDot} style={{ backgroundColor: item.color }} />
      <span>{info.text}</span>
    </span>
  );
}

export function NegocioGuard({ children }) {
  useNegocioBridge();
  const { user, loading } = useContext(AuthContext);

  useEffect(() => {
    if (!user?.companyId) return undefined;
    let cancelled = false;
    let sequence = 0;
    const reloadFormat = async () => {
      const request = ++sequence;
      try {
        const [tz, settingsRes] = await Promise.all([
          getCampaignTimezone(user.companyId),
          negocioResources.load(resourceKey(user.companyId, "settings"), async () => {
            const { data } = await api.get("/negocio/settings");
            return data;
          }).then((data) => ({ data })),
        ]);
        if (cancelled || request !== sequence) return;
        const data = settingsRes.data || {};
        setNegocioFormat({
          timezone: tz || "America/Bogota",
          currency: data.currencyLocal || "COP",
          symbol: data.currencySymbol || defaultSymbol(data.currencyLocal || "COP"),
          currencyFx: data.currencyFx || "USD",
          exchangeRate: Number(data.exchangeRate || 1),
          pointsSystemEnabled: data.pointsSystemEnabled !== false && data.pointsSystemEnabled !== 0,
          pointsEquivalence: Number(data.pointsEquivalence || 1000),
          profitPercent: Number(data.profitPercent != null ? data.profitPercent : 50),
        });
      } catch {
        /* keep defaults */
      }
    };
    reloadFormat();
    const unsubscribe = subscribeNegocio((change) => {
      if (change.companyId != null && String(change.companyId) !== String(user.companyId)) return;
      if (change.paths.has("settings")) reloadFormat();
    });
    return () => {
      unsubscribe();
      cancelled = true;
    };
  }, [user?.companyId]);

  if (loading) return null;
  // Mismo criterio que el menú (AccordionMenu) y el backend (canUseNegocio): super o admin con plan useNegocio.
  const allowed =
    isSystemOwner(user) || (isCompanyAdmin(user) && localStorage.getItem("showNegocio") === "true");
  if (!allowed) return <Redirect to="/" />;
  return children;
}

export function StatusChip({ value, prefix = "negocioModule.status." }) {
  const classes = useStyles();
  if (!value) return null;
  const raw = i18n.t(`${prefix}${value}`, { defaultValue: String(value) });
  const label = typeof raw === "string" || typeof raw === "number" ? raw : String(value);
  return (
    <Chip
      size="small"
      className={classes.chip}
      label={label}
      style={{ backgroundColor: statusColor(value), color: "#fff" }}
    />
  );
}

export function DialogClose({ onClose, disabled }) {
  if (!onClose) return null;
  return (
    <IconButton
      size="small"
      aria-label={tNegocio("close")}
      onClick={onClose}
      disabled={!!disabled}
      style={{ position: "absolute", top: 8, right: 8, zIndex: 2, width: 32, height: 32, padding: 4 }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );
}

export function KpiTile({ icon: Icon, label, value, hint, onClick, tone }) {
  const classes = useStyles();
  const activate = onClick
    ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }
    : undefined;
  return (
    <div
      className={classes.kpiCard}
      onClick={onClick}
      onKeyDown={activate}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {Icon ? (
        <span className={`${classes.kpiIcon}${tone ? ` ${classes[tone]}` : ""}`}>
          <Icon />
        </span>
      ) : null}
      <span className={classes.kpiBody}>
        <span className={classes.kpiLabel}>{label}</span>
        <span className={classes.kpiValue}>{value}</span>
        {hint || null}
      </span>
    </div>
  );
}

export function PageFrame({ title, subtitle, actions, children, lockBody }) {
  const classes = useStyles();
  return (
    <MainContainer>
      <div className={classes.pageRoot}>
        <MainHeader>
          <Title subtitle={subtitle}>{title}</Title>
          <MainHeaderButtonsWrapper>{actions}</MainHeaderButtonsWrapper>
        </MainHeader>
        <div className={`${classes.contentScroll}${lockBody ? ` ${classes.contentLock}` : ""}`}>{children}</div>
      </div>
    </MainContainer>
  );
}

export function SearchField({ value, onChange, placeholder, fullWidth }) {
  const classes = useStyles();
  return (
    <TextField
      size="small"
      variant="outlined"
      className={`${classes.searchField}${fullWidth ? ` ${classes.searchFull}` : ""}`}
      placeholder={placeholder || tNegocio("search")}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton size="small" aria-label="clear" onClick={() => onChange("")}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}

export function FilterChips({ value, onChange, options }) {
  const classes = useStyles();
  return (
    <div className={classes.filters}>
      {options.map((option) => (
        <Chip
          key={String(option.value)}
          size="small"
          clickable
          color={value === option.value ? "primary" : "default"}
          variant={value === option.value ? "default" : "outlined"}
          icon={option.icon || undefined}
          label={option.label}
          onClick={() => onChange(option.value)}
        />
      ))}
    </div>
  );
}

export function usePager(rows, pageSize = 25) {
  const list = Array.isArray(rows) ? rows : [];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const resetKey = `${list.length}:${list[0]?.id ?? list[0]?.contactId ?? ""}:${list[list.length - 1]?.id ?? list[list.length - 1]?.contactId ?? ""}`;
  useEffect(() => {
    setPage(0);
  }, [resetKey, rowsPerPage]);
  const maxPage = Math.max(0, Math.ceil(list.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);
  const slice = useMemo(
    () => list.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [list, safePage, rowsPerPage]
  );
  const offset = safePage * rowsPerPage;
  return { page: safePage, setPage, rowsPerPage, setRowsPerPage, slice, count: list.length, offset };
}

export function NegocioPager({ count, page, rowsPerPage, onChangePage, onChangeRowsPerPage, hideBelow }) {
  const classes = useStyles();
  if (!count) return null;
  if (hideBelow && count <= hideBelow) return null;
  return (
    <TablePagination
      component="div"
      className={classes.pager}
      count={count}
      page={page}
      onChangePage={(_, next) => onChangePage(next)}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={(e) => {
        onChangeRowsPerPage(parseInt(e.target.value, 10));
        onChangePage(0);
      }}
      rowsPerPageOptions={[10, 25, 50, 100]}
      labelRowsPerPage={tNegocio("pager.rows")}
      labelDisplayedRows={({ from, to, count: total }) =>
        tNegocio("pager.shown").replace("{from}", from).replace("{to}", to).replace("{count}", total)
      }
    />
  );
}

export function LoadingHint() {
  const classes = useStyles();
  return (
    <div className={classes.loadingState}>
      <CircularProgress size={28} color="primary" />
    </div>
  );
}

export function EmptyHint({ title, subtitle, actionLabel, onAction }) {
  const classes = useStyles();
  return (
    <div className={classes.emptyState}>
      <div className={classes.emptyTitle}>{title}</div>
      {subtitle ? <div className={classes.emptySubtitle}>{subtitle}</div> : null}
      {onAction && actionLabel ? (
        <Button color="primary" variant="contained" size="small" onClick={onAction} style={{ marginTop: 8 }}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

export function RecordsState({ loading, count, emptyTitle, emptySubtitle, children }) {
  if (loading && !count) return <LoadingHint />;
  if (!count) return <EmptyHint title={emptyTitle} subtitle={emptySubtitle} />;
  return children;
}

export function ContactAvatar({ name, number, profilePicUrl, size = 42 }) {
  const classes = useStyles();
  const raw = String(profilePicUrl || "");
  const photo = raw && !raw.includes("nopicture") ? getProfilePicUrl(raw) : null;
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [photo]);
  const src = photo && !broken ? photo : PROFILE_PLACEHOLDER;
  return (
    <Avatar
      className={classes.contactAvatar}
      style={{
        width: size,
        height: size,
        backgroundColor: photo && !broken ? colorFromKey(number || name) : "#e2e8f0",
        fontSize: Math.max(12, Math.round(size * 0.32)),
      }}
      src={src}
      alt={name || ""}
      imgProps={{ onError: () => setBroken(true) }}
    >
      {getInitials(name || "?")}
    </Avatar>
  );
}

export function RelatedLinks({ items }) {
  const classes = useStyles();
  const history = useHistory();
  const list = (items || []).filter((item) => item && item.to && item.label);
  if (!list.length) return null;
  return (
    <div className={classes.relatedLinks}>
      {list.map((item) => (
        <Chip
          key={item.to}
          size="small"
          clickable
          color="primary"
          variant="outlined"
          label={item.label}
          onClick={(e) => {
            e.stopPropagation();
            history.push(item.to);
          }}
        />
      ))}
    </div>
  );
}

export function InfoDialog({ open, onClose, title, subtitle, avatar, chips, fields, extra, actions, hero }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
      <DialogClose onClose={onClose} />
      {hero ? (
        <DialogTitle disableTypography className={`${classes.heroTitle} dialogHeroTitle`}>
          {hero}
        </DialogTitle>
      ) : (
        <DialogTitle>
          <div className={classes.productHead} style={{ alignItems: "center" }}>
            {avatar}
            <div>
              <div>{title}</div>
              {subtitle ? <div className={classes.captionBlock}>{subtitle}</div> : null}
            </div>
          </div>
        </DialogTitle>
      )}
      <DialogContent dividers>
        {chips ? <div className={classes.detailMeta}>{chips}</div> : null}
        {(fields || []).filter((field) => field && field.label).map((field) => (
          <div key={field.label} className={classes.infoField}>
            <div className={classes.captionBlock}>{field.label}</div>
            <div>{field.value == null || field.value === "" ? "—" : field.value}</div>
          </div>
        ))}
        {extra}
      </DialogContent>
      {actions ? <DialogActions>{actions}</DialogActions> : null}
    </Dialog>
  );
}
